import { useEffect, useRef, useState } from 'react'
import { chatError, withChatLock } from '@/lib/support-chat/client'
import { useConversationRequest } from './useConversationRequest'
import type { ChatVerification } from '@/lib/support-chat/types'
import {
  supportButton,
  supportField,
} from '../../../app/account/support/supportTypes'

export function ChatEmailPanel({
  browserContext,
  verification,
  accountEmail,
  onChange,
}: {
  browserContext: string
  verification: ChatVerification | null
  accountEmail?: string
  onChange: () => Promise<unknown>
}) {
  const [expanded, setExpanded] = useState(Boolean(verification))
  const [address, setAddress] = useState(accountEmail ?? '')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [now, setNow] = useState(0)
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [])
  const request = useRef<{ requestId: string; address: string } | null>(null)
  const sendRequest = useConversationRequest(browserContext)
  const effectiveAddress = accountEmail ?? address
  async function perform(action: 'requestEmail' | 'confirmEmail') {
    setBusy(true)
    setError(null)
    try {
      let input: object
      if (action === 'requestEmail') {
        if (request.current?.address !== effectiveAddress)
          request.current = {
            requestId: crypto.randomUUID(),
            address: effectiveAddress,
          }
        input = request.current
      } else {
        if (!verification) return
        input = { id: verification.id, code }
      }
      await withChatLock(() =>
        sendRequest<ChatVerification>({
          method: 'POST',
          browserContext,
          body: { action, input },
        })
      )
      if (action === 'requestEmail') request.current = null
      setCode('')
      await onChange()
    } catch (error) {
      setError(chatError(error))
    } finally {
      setBusy(false)
    }
  }
  if (verification?.state === 'Verified')
    return (
      <p className="rounded-lg bg-emerald-50 p-3 text-xs leading-5 text-emerald-900">
        Email follow-up is ready. Future replies will also go to{' '}
        {verification.address}. Earlier chat messages have not been emailed.
      </p>
    )
  const pending = verification?.state === 'Pending'
  const delivery = verification?.delivery
  const waiting =
    pending &&
    ['Queued', 'Failed', 'Cancelled', 'None'].includes(delivery ?? '')
  const deliveryMessage =
    delivery === 'Queued'
      ? 'Your verification email is queued for sending.'
      : ['Failed', 'Cancelled', 'None'].includes(delivery ?? '')
        ? 'This verification email was not sent. Request a new code when the option appears below.'
        : ['Sending', 'Unknown'].includes(delivery ?? '')
          ? `Delivery is not yet confirmed. If a code arrives at ${verification?.address}, you can enter it here.`
          : `Enter the code sent to ${verification?.address}. It expires after 15 minutes.`
  return (
    <section className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
      <button
        className="font-semibold text-gray-800 underline underline-offset-2"
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        Get replies by email
      </button>
      {expanded && (
        <div className="mt-3 space-y-3">
          <p className="text-xs leading-5 text-gray-600">
            Verify your address to continue this conversation after you leave.
            This does not link other tickets.
          </p>
          {pending ? (
            <form
              className="space-y-3"
              onSubmit={event => {
                event.preventDefault()
                void perform('confirmEmail')
              }}
            >
              <p className="text-xs text-gray-600">{deliveryMessage}</p>
              <label className="block text-sm">
                Verification code
                <input
                  className={supportField}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{8}"
                  maxLength={8}
                  value={code}
                  onChange={event =>
                    setCode(event.target.value.replace(/\D/g, ''))
                  }
                  disabled={busy || waiting}
                  required
                />
              </label>
              <button
                className={supportButton}
                disabled={busy || waiting || code.length !== 8}
              >
                {busy ? 'Checking…' : 'Verify email'}
              </button>
            </form>
          ) : verification ? (
            <p className="text-xs text-gray-600">
              This code is no longer available. Request a new one below.
            </p>
          ) : null}
          {(!pending ||
            now >= new Date(verification.canResendAtUtc).getTime()) && (
            <form
              className="space-y-3"
              onSubmit={event => {
                event.preventDefault()
                void perform('requestEmail')
              }}
            >
              <label className="block text-sm">
                Email address
                <input
                  className={supportField}
                  type="email"
                  autoComplete="email"
                  value={effectiveAddress}
                  onChange={event => {
                    setAddress(event.target.value)
                    request.current = null
                  }}
                  maxLength={320}
                  required
                  readOnly={Boolean(accountEmail)}
                  disabled={busy}
                />
              </label>
              <button
                className={supportButton}
                disabled={busy || !effectiveAddress}
              >
                {busy
                  ? 'Requesting…'
                  : verification
                    ? 'Request a new code'
                    : 'Send verification code'}
              </button>
            </form>
          )}
          {error && (
            <p role="alert" className="text-xs text-red-700">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
